import {
  GroupShapeIcon,
  type GroupShape,
} from "@/components/shapes/GroupShapeIcon";
import { groupColors } from "@/configs/map/groups";
import { useActiveDataflowId } from "@/stores/dataflow";
import { useMapStore } from "@/stores/map";
import { logger } from "@/utils/debug";
import {
  Button,
  ColorInput,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect } from "react";

export function WorkspaceModalGroupConfig({
  onClose,
  title = "Configure Group",
  groupValue,
  initialName,
  initialColor,
  initialShape,
}: {
  onClose: () => void;
  title?: string;
  groupValue?: string;
  initialName?: string;
  initialColor?: string;
  initialShape?: GroupShape;
}) {
  const activeDataflowId = useActiveDataflowId();
  const setGroupConfig = useMapStore((state) => state.setGroupConfig);

  const form = useForm<{
    name: string;
    color: string;
    shape: GroupShape;
  }>({
    initialValues: {
      name: initialName || groupValue || "",
      color: initialColor || "#003399",
      shape: initialShape || "circle",
    },
    validate: {
      name: (value) =>
        !value || value.trim() === "" ? "Name is required" : null,
      color: (value) => (!value ? "Color is required" : null),
    },
  });

  useEffect(() => {
    if (
      initialName !== undefined ||
      initialColor !== undefined ||
      initialShape !== undefined ||
      groupValue
    ) {
      form.setValues({
        name: initialName || groupValue || "",
        color: initialColor || "#003399",
        shape: initialShape || "circle",
      });
    }
  }, [initialName, initialColor, initialShape, groupValue]);

  const handleSubmit = (values: {
    name: string;
    color: string;
    shape: GroupShape;
  }) => {
    if (!activeDataflowId || !groupValue) {
      logger.error(
        "Cannot save group config: missing activeDataflowId or groupValue",
        {
          activeDataflowId,
          groupValue,
        }
      );
      return;
    }

    setGroupConfig(activeDataflowId, groupValue, {
      name: values.name.trim(),
      color: values.color,
      shape: values.shape,
    });

    onClose();
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={title}
      size="md"
      closeOnClickOutside={false}
      centered
    >
      <form
        onSubmit={form.onSubmit(handleSubmit, (errors) => {
          logger.error("Form validation errors:", errors);
        })}
      >
        <Stack gap="md">
          <TextInput
            label="Group Name"
            placeholder="Enter group name"
            {...form.getInputProps("name")}
            required
            error={form.errors.name}
          />

          <ColorInput
            label="Group Color"
            format="hex"
            swatches={groupColors}
            {...form.getInputProps("color")}
            required
            error={form.errors.color}
          />

          <div>
            <Text size="sm" fw={500} mb="xs">
              Shape
            </Text>
            <SegmentedControl
              fullWidth
              data={[
                {
                  value: "circle",
                  label: (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <GroupShapeIcon
                        shape="circle"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "square",
                  label: (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <GroupShapeIcon
                        shape="square"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "triangle",
                  label: (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <GroupShapeIcon
                        shape="triangle"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "star",
                  label: (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <GroupShapeIcon
                        shape="star"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "diamond",
                  label: (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <GroupShapeIcon
                        shape="diamond"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
              ]}
              {...form.getInputProps("shape")}
            />
          </div>

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
